import React, { useState, useEffect } from 'react';
import { Modal, Button, message, Select, Spin, Typography } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { makePostCall, makeGetCall, getUserDetails } from '../utils/helper';

const { Text } = Typography;

interface Modality {
  en_id: string;
  en_node_name: string;
  en_ip_address: string;
  en_port: string;
  en_ae_title: string;
  en_location: string;
  en_operations: string;
  en_comments: string;
}

interface ExportStudyModalProps {
  visible: boolean;
  onCancel: () => void;
  studyUID: string;
  onSuccess?: () => void;
}

const ExportStudyModal: React.FC<ExportStudyModalProps> = ({
  visible,
  onCancel,
  studyUID,
  onSuccess,
}) => {
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalities, setModalities] = useState<Modality[]>([]);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      fetchModalities();
    }
  }, [visible]);

  const fetchModalities = async () => {
    try {
      setLoading(true);
      const res = await makeGetCall('/get-modality-list');

      if (res.data?.success !== false && Array.isArray(res.data?.data)) {
        setModalities(res.data.data);
      } else {
        throw new Error(res.data?.message || 'Failed to load modalities');
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to load modalities';
      console.error('Error fetching modalities:', error);
      message.error(`Error loading modalities: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!selectedNode) {
      message.warning('Please select a destination node');
      return;
    }

    const selectedModality = modalities.find(m => m.en_id === selectedNode);
    if (!selectedModality) {
      message.error('Selected node not found');
      return;
    }

    try {
      setExporting(true);
      const userDetails = getUserDetails();
      const res = await makePostCall('/push-studies', {
        study_uid: studyUID,
        node_id: parseInt(selectedNode),
        user_id: userDetails?.username,
      });

      if (res.data?.success || res.data?.message) {
        message.success('Study pushed successfully');
        setSelectedNode(null);
        onSuccess?.();
        onCancel();
      } else {
        throw new Error(res.data?.message || 'Failed to push study');
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to push study';
      console.error('Error pushing study:', error);
      message.error(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ExportOutlined />
          <span>Push Study to Node</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={exporting || loading}>
          Cancel
        </Button>,
        <Button
          key="push"
          type="primary"
          onClick={handleExport}
          loading={exporting}
          disabled={exporting || loading || !selectedNode}
          icon={<ExportOutlined />}
        >
          Push Study
        </Button>,
      ]}
      width={500}
      centered
      maskClosable={!exporting}
      closable={!exporting}
    >
      <Spin spinning={loading} tip="Loading nodes...">
        <div style={{ padding: '16px 0' }}>
          <div style={{ marginBottom: '16px' }}>
            <Text strong>Study UID:</Text>
            <div
              style={{
                background: '#f5f5f5',
                padding: '8px 12px',
                borderRadius: '4px',
                marginTop: '8px',
                wordBreak: 'break-all',
                fontSize: '12px',
              }}
            >
              {studyUID}
            </div>
          </div>

          <div>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Destination Node
            </Text>
            <Select
              showSearch
              placeholder="Select a node to push to"
              value={selectedNode}
              onChange={setSelectedNode}
              disabled={loading || exporting}
              style={{ width: '100%' }}
              optionFilterProp="label"
              options={modalities.map(modality => ({
                value: modality.en_id,
                label: `${modality.en_node_name} (${modality.en_ae_title})`,
              }))}
            />
          </div>

          {selectedNode && (
            <div
              style={{
                marginTop: '12px',
                padding: '8px 12px',
                background: '#e6f7ff',
                borderRadius: '4px',
                fontSize: '12px',
              }}
            >
              <Text type="secondary">
                {modalities.find(m => m.en_id === selectedNode)?.en_ip_address &&
                  `IP: ${modalities.find(m => m.en_id === selectedNode)?.en_ip_address}`}
              </Text>
            </div>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default ExportStudyModal;
